import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import { useCurrentUser } from '../../context/CurrentUserContext';
import RoleGate from '../RoleGate';
import MailForwarderRuleForm from './MailForwarderRuleForm';
import MailForwarderQuickCreateModal from './MailForwarderQuickCreateModal';

const BASE_PATH = `${QUALITY_SERVICE_API_URL}/hub/mail-forwarder-rules`;
const BASE_URL = '/admin/mail-forwarders';

function canManageRule(rule, currentUser) {
  if (!currentUser) return false;
  if (currentUser.hubRole === 'admin') return true;
  return rule.hubUser?.id === currentUser.id;
}

function AdminMailForwarderRules() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.hubRole === 'admin';
  const [rules, setRules] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [error, setError] = useState(null);
  const [quickCreating, setQuickCreating] = useState(false);
  const [quickCreated, setQuickCreated] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const editing = id === undefined ? null
    : id === 'new' ? {}
    : rules.find(r => String(r.id) === id) ?? null;

  // Bad/stale id (e.g. deleted item, typo'd link) -> back to the list,
  // but only once loading has actually finished.
  useEffect(() => {
    if (!loading && id && id !== 'new' && !editing) navigate(BASE_URL, { replace: true });
  }, [loading, id, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleRules = isAdmin && showAll
    ? rules
    : rules.filter(rule => rule.hubUser?.id === currentUser?.id);

  const load = () => {
    setLoading(true);
    api.get(BASE_PATH)
      .then(r => setRules(r.data))
      .catch(() => setError('Failed to load mail forwarder rules.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSave = async (data) => {
    setSaving(true);
    setFormError(null);
    try {
      if (editing.id) {
        await api.put(`${BASE_PATH}/${editing.id}`, data);
      } else {
        await api.post(BASE_PATH, data);
      }
      navigate(BASE_URL);
      load();
    } catch (err) {
      if (err.response?.status === 409) {
        setFormError('A forwarder rule for this source address already exists.');
      } else {
        setFormError(err.response?.data?.message || 'Save failed. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleQuickCreate = async () => {
    setQuickCreating(true);
    setError(null);
    try {
      const { data } = await api.post(`${BASE_PATH}/quick-create`);
      setQuickCreated(data);
      load();
    } catch {
      setError('Quick create failed.');
    } finally {
      setQuickCreating(false);
    }
  };

  const handleUndoQuickCreate = async () => {
    setUndoing(true);
    setError(null);
    try {
      await api.delete(`${BASE_PATH}/${quickCreated.id}`);
      setQuickCreated(null);
      load();
    } catch {
      setError('Undo failed.');
    } finally {
      setUndoing(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete forwarder "${rule.sourceAddress}"? This cannot be undone.`)) return;
    try {
      await api.delete(`${BASE_PATH}/${rule.id}`);
      load();
    } catch {
      setError('Delete failed.');
    }
  };

  return (
    <div className="admin-campaigns">
      <div className="admin-section-toolbar">
        <h2 className="admin-section-title">Mail Forwarders</h2>
        <RoleGate allow={['admin']}>
          <label className="cf-section-check">
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Show all forwarders
          </label>
        </RoleGate>
        <button className="admin-btn-secondary" onClick={() => navigate(`${BASE_URL}/blocked-senders`)}>Blocked Senders</button>
        <RoleGate allow={['admin']}>
          <button className="admin-btn-secondary" onClick={() => navigate(`${BASE_URL}/log`)}>View Log</button>
        </RoleGate>
        <RoleGate allow={['admin', 'write']}>
          <button className="admin-btn-secondary" disabled={quickCreating} onClick={handleQuickCreate}>
            {quickCreating ? 'Creating…' : 'Quick Create'}
          </button>
        </RoleGate>
        <RoleGate allow={['admin', 'write']}>
          <button className="admin-btn-primary" onClick={() => navigate(`${BASE_URL}/new`)}>+ New Forwarder</button>
        </RoleGate>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Recipients</th>
              <th>Visibility</th>
              <th>Owner</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRules.map(rule => (
              <tr key={rule.id}>
                <td className="admin-td-name">{rule.sourceAddress}</td>
                <td className="admin-td-leads">
                  <div className="admin-pill-list">
                    {rule.destinationAddresses.map(addr => (
                      <code key={addr} className="admin-label-chip">{addr}</code>
                    ))}
                  </div>
                </td>
                <td><code className="admin-label-chip">{rule.visibility}</code></td>
                <td>{rule.hubUser?.email ?? '—'}</td>
                <td>
                  <span className={rule.enabled ? 'atm-active-badge' : 'atm-archived-badge'}>
                    {rule.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="mfr-td-actions">
                  {canManageRule(rule, currentUser) && (
                    <span className="mfr-actions-group">
                      <button className="admin-btn-edit" onClick={() => navigate(`${BASE_URL}/${rule.id}`)}>Edit</button>
                      <button className="admin-btn-delete" onClick={() => handleDelete(rule)}>Delete</button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {visibleRules.length === 0 && (
              <tr><td colSpan={6} className="admin-empty">No mail forwarder rules yet.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {editing !== null && (
        <MailForwarderRuleForm
          rule={editing}
          onSave={handleSave}
          onCancel={() => { navigate(BASE_URL); setFormError(null); }}
          saving={saving}
          error={formError}
        />
      )}

      {quickCreated && (
        <MailForwarderQuickCreateModal
          address={quickCreated.sourceAddress}
          onClose={() => setQuickCreated(null)}
          onUndo={handleUndoQuickCreate}
          undoing={undoing}
        />
      )}
    </div>
  );
}

export default AdminMailForwarderRules;
